/**
 * PlantillasPage — CRUD de plantillas de mantenimiento.
 */
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, Paper, IconButton, Stack, Switch,
  TextField, MenuItem, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';
import { usePlantillas } from './shared/useFlota';
import { API_BASE, getAuthHeaders, getEmpresaId, CATEGORIAS } from './shared/format';

function emptyForm() {
  return {
    nombre: '', categoria: 'motor', descripcion: '',
    frecuencia_dias: '', fecha_fija_mes: '', fecha_fija_dia: '',
    duracion_estimada_horas: '',
    requiere_dique: false, requiere_proveedor_externo: false, obligatorio_para_zarpe: false,
    activo: true,
  };
}

export default function PlantillasPage() {
  const { plantillas, isLoading, refetch } = usePlantillas();
  const { toast, ToastContainer } = useToast();
  const [editando, setEditando] = useState(null);  // null | { plantilla?, form }
  const [confirmDel, setConfirmDel] = useState(null);

  const visibles = useMemo(() => plantillas.filter(p => p.activo !== false), [plantillas]);

  const guardar = async () => {
    if (!editando) return;
    const form = editando.form;
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    const body = {
      empresa_id: getEmpresaId(),
      nombre: form.nombre.trim(),
      categoria: form.categoria,
      descripcion: form.descripcion || null,
      frecuencia_dias: form.frecuencia_dias ? Number(form.frecuencia_dias) : null,
      fecha_fija_mes: form.fecha_fija_mes ? Number(form.fecha_fija_mes) : null,
      fecha_fija_dia: form.fecha_fija_dia ? Number(form.fecha_fija_dia) : null,
      duracion_estimada_horas: form.duracion_estimada_horas ? Number(form.duracion_estimada_horas) : null,
      requiere_dique: !!form.requiere_dique,
      requiere_proveedor_externo: !!form.requiere_proveedor_externo,
      obligatorio_para_zarpe: !!form.obligatorio_para_zarpe,
      activo: !!form.activo,
    };
    const url = editando.plantilla
      ? `${API_BASE}/turismo/mantenimiento/plantillas/${editando.plantilla.id}`
      : `${API_BASE}/turismo/mantenimiento/plantillas`;
    const r = await fetch(url, {
      method: editando.plantilla ? 'PUT' : 'POST',
      headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    toast.success(editando.plantilla ? 'Plantilla actualizada' : 'Plantilla creada');
    setEditando(null);
    refetch();
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    await fetch(`${API_BASE}/turismo/mantenimiento/plantillas/${confirmDel.id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setConfirmDel(null);
    toast.success('Plantilla desactivada');
    refetch();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Plantillas de mantenimiento</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Catálogo de tareas recurrentes ({visibles.length} activas).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setEditando({ form: emptyForm() })}
          sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
          Nueva plantilla
        </Button>
      </Box>

      {!isLoading && visibles.length === 0 ? (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.85)' }}>
          <EmptyState icon={<ListAltIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
            title="Sin plantillas"
            message="Crea plantillas para tareas recurrentes (cambio de aceite, inspecciones, etc.)" />
        </Paper>
      ) : (
        <Stack spacing={1}>
          {visibles.map(p => {
            const cat = CATEGORIAS.find(c => c.value === p.categoria) || CATEGORIAS[CATEGORIAS.length - 1];
            return (
              <Paper key={p.id} sx={{
                p: 1.5, borderRadius: 2, borderLeft: `4px solid ${cat.color}`,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{p.nombre}</Typography>
                    <Chip size="small" label={cat.label}
                      sx={{ bgcolor: `${cat.color}20`, color: cat.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                    {p.obligatorio_para_zarpe && (
                      <Chip size="small" label="Obligatorio para zarpe"
                        sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 18 }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
                    {p.frecuencia_dias && `Cada ${p.frecuencia_dias} días`}
                    {p.fecha_fija_mes && p.fecha_fija_dia && `Anual: ${p.fecha_fija_dia}/${p.fecha_fija_mes}`}
                    {p.requiere_dique && ' · Requiere dique'}
                    {p.requiere_proveedor_externo && ' · Proveedor externo'}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setEditando({ plantilla: p, form: {
                  nombre: p.nombre, categoria: p.categoria, descripcion: p.descripcion || '',
                  frecuencia_dias: p.frecuencia_dias || '',
                  fecha_fija_mes: p.fecha_fija_mes || '', fecha_fija_dia: p.fecha_fija_dia || '',
                  duracion_estimada_horas: p.duracion_estimada_horas || '',
                  requiere_dique: p.requiere_dique, requiere_proveedor_externo: p.requiere_proveedor_externo,
                  obligatorio_para_zarpe: p.obligatorio_para_zarpe, activo: p.activo,
                } })}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" onClick={() => setConfirmDel(p)} sx={{ color: '#ef4444' }}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Paper>
            );
          })}
        </Stack>
      )}

      {editando && (
        <SlideOver open onClose={() => setEditando(null)}
          title={editando.plantilla ? 'Editar plantilla' : 'Nueva plantilla'}
          icon={<ListAltIcon />}
          footer={
            <>
              <Button onClick={() => setEditando(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
              <Button variant="contained" onClick={guardar} disableElevation
                sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
                Guardar
              </Button>
            </>
          }>
          <Stack spacing={1.5}>
            <TextField size="small" label="Nombre *" value={editando.form.nombre}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, nombre: e.target.value } })} />
            <TextField size="small" select label="Categoría" value={editando.form.categoria}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, categoria: e.target.value } })}>
              {CATEGORIAS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField size="small" multiline minRows={2} label="Descripción"
              value={editando.form.descripcion}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, descripcion: e.target.value } })} />

            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', mt: 1 }}>
              Frecuencia (usa solo una)
            </Typography>
            <TextField size="small" type="number" label="Cada N días (recurrente)"
              value={editando.form.frecuencia_dias}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, frecuencia_dias: e.target.value, fecha_fija_mes: '', fecha_fija_dia: '' } })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField size="small" type="number" label="Mes fijo (1-12)"
                value={editando.form.fecha_fija_mes}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, fecha_fija_mes: e.target.value, frecuencia_dias: '' } })} />
              <TextField size="small" type="number" label="Día fijo (1-31)"
                value={editando.form.fecha_fija_dia}
                onChange={e => setEditando({ ...editando, form: { ...editando.form, fecha_fija_dia: e.target.value, frecuencia_dias: '' } })} />
            </Box>

            <TextField size="small" type="number" label="Duración estimada (horas)"
              value={editando.form.duracion_estimada_horas}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, duracion_estimada_horas: e.target.value } })} />

            <FormControlLabel control={<Switch checked={editando.form.requiere_dique}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, requiere_dique: e.target.checked } })} />}
              label="Requiere dique" />
            <FormControlLabel control={<Switch checked={editando.form.requiere_proveedor_externo}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, requiere_proveedor_externo: e.target.checked } })} />}
              label="Requiere proveedor externo" />
            <FormControlLabel control={<Switch checked={editando.form.obligatorio_para_zarpe}
              onChange={e => setEditando({ ...editando, form: { ...editando.form, obligatorio_para_zarpe: e.target.checked } })} />}
              label="Obligatorio para zarpe (bloquea operación si está vencido)" />
          </Stack>
        </SlideOver>
      )}

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={eliminar}
        title="¿Desactivar plantilla?"
        message={confirmDel ? `"${confirmDel.nombre}" dejará de estar disponible para nuevos mantenimientos. Los registros históricos se conservan.` : ''}
        confirmText="Desactivar" severity="warning" />

      <ToastContainer />
    </Box>
  );
}
